/**
 * src/domain/attention/human-pulse-classifier.ts
 *
 * Human Pulse 5-Signal Kinetic Vector Classifier.
 * Non-camera, privacy-preserving proof-of-human-attention.
 *
 * Evaluates cursor / touch trajectory points: P_i = (x_i, y_i, t_i)
 * Classifies as 'HUMAN' if at least 4 of 5 independent physiological signals pass:
 * 1. Temporal Bounds: 600ms <= duration <= 2800ms
 * 2. Kinetic Micro-Tremor (Jitter): 0.15 <= J_kinetic <= 0.85
 * 3. Directional Inflections: count >= 3
 * 4. Angular Shannon Entropy: H_theta >= 0.35 bits/sample (across 8 directional bins)
 * 5. Acceleration Feasibility: v_max < 8000 px/sec
 */

export interface TrajectoryPoint {
  x: number;
  y: number;
  t: number; // Elapsed milliseconds from start of gesture
}

export interface ClassificationReport {
  classification: 'HUMAN' | 'SUSPECT';
  passedCount: number;
  checks: {
    temporalBounds: boolean;
    microTremor: boolean;
    directionalInflections: boolean;
    entropy: boolean;
    accelerationSmoothness: boolean;
  };
  metrics: {
    durationMs: number;
    jitter: number;
    inflections: number;
    angularEntropy: number;
    maxVelocityPxSec: number;
  };
}

export class HumanPulseClassifier {
  public static readonly MIN_POINTS = 15;
  public static readonly MIN_DURATION_MS = 600;
  public static readonly MAX_DURATION_MS = 2800;
  public static readonly MIN_JITTER = 0.15;
  public static readonly MAX_JITTER = 0.85;
  public static readonly MIN_INFLECTIONS = 3;
  public static readonly MIN_ENTROPY = 0.35;
  public static readonly MAX_VELOCITY_PX_SEC = 8000;

  /**
   * Evaluates gesture trajectory and classifies as HUMAN or SUSPECT.
   */
  public static classifyTrajectory(points: TrajectoryPoint[], durationMs: number): ClassificationReport {
    if (!points || points.length < this.MIN_POINTS) {
      return {
        classification: 'SUSPECT',
        passedCount: 0,
        checks: {
          temporalBounds: false,
          microTremor: false,
          directionalInflections: false,
          entropy: false,
          accelerationSmoothness: false,
        },
        metrics: {
          durationMs,
          jitter: 0,
          inflections: 0,
          angularEntropy: 0,
          maxVelocityPxSec: 0,
        },
      };
    }

    const jitter = this.calculateJitter(points);
    const inflections = this.countInflections(points);
    const angularEntropy = this.calculateAngularEntropy(points);
    const maxVelocityPxSec = this.calculateMaxVelocity(points);

    const checks = {
      temporalBounds: durationMs >= this.MIN_DURATION_MS && durationMs <= this.MAX_DURATION_MS,
      microTremor: jitter >= this.MIN_JITTER && jitter <= this.MAX_JITTER,
      directionalInflections: inflections >= this.MIN_INFLECTIONS,
      entropy: angularEntropy >= this.MIN_ENTROPY,
      accelerationSmoothness: maxVelocityPxSec < this.MAX_VELOCITY_PX_SEC,
    };

    const passedCount = Object.values(checks).filter(Boolean).length;
    const classification = passedCount >= 4 ? 'HUMAN' : 'SUSPECT';

    return {
      classification,
      passedCount,
      checks,
      metrics: {
        durationMs,
        jitter,
        inflections,
        angularEntropy,
        maxVelocityPxSec,
      },
    };
  }

  /**
   * Signal 2: Kinetic Tremor Jitter.
   * Root-mean-square variation in velocity deltas across sample frames.
   */
  public static calculateJitter(points: TrajectoryPoint[]): number {
    if (points.length < 3) return 0;
    let diffSum = 0;

    for (let i = 2; i < points.length; i++) {
      const dt1 = points[i - 1].t - points[i - 2].t || 1;
      const dt2 = points[i].t - points[i - 1].t || 1;

      const v1 = Math.hypot(points[i - 1].x - points[i - 2].x, points[i - 1].y - points[i - 2].y) / dt1;
      const v2 = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y) / dt2;

      diffSum += Math.abs(v2 - v1);
    }

    return diffSum / (points.length - 2);
  }

  /**
   * Signal 3: Directional Inflections.
   * Counts micro-corrections (sign changes in velocity).
   */
  public static countInflections(points: TrajectoryPoint[]): number {
    if (points.length < 3) return 0;
    let inflections = 0;

    for (let i = 2; i < points.length; i++) {
      const dx1 = points[i - 1].x - points[i - 2].x;
      const dx2 = points[i].x - points[i - 1].x;
      const dy1 = points[i - 1].y - points[i - 2].y;
      const dy2 = points[i].y - points[i - 1].y;

      // Check inflection in either axis
      if ((dx1 > 0 && dx2 < 0) || (dx1 < 0 && dx2 > 0) || (dy1 > 0 && dy2 < 0) || (dy1 < 0 && dy2 > 0)) {
        inflections++;
      }
    }

    return inflections;
  }

  /**
   * Signal 4: Angular Shannon Entropy (H_theta).
   * Discretizes movement directions into 8 angular bins (45 degrees each).
   */
  public static calculateAngularEntropy(points: TrajectoryPoint[]): number {
    if (points.length < 2) return 0;
    const bins = new Array(8).fill(0);
    let totalSamples = 0;

    for (let i = 1; i < points.length; i++) {
      const dx = points[i].x - points[i - 1].x;
      const dy = points[i].y - points[i - 1].y;

      // Ignore zero-movement frames
      if (dx === 0 && dy === 0) continue;

      const angle = Math.atan2(dy, dx); // [-PI, PI]
      const normalizedAngle = angle + Math.PI; // [0, 2*PI]
      const binIndex = Math.min(7, Math.floor((normalizedAngle / (2 * Math.PI)) * 8));

      bins[binIndex]++;
      totalSamples++;
    }

    if (totalSamples === 0) return 0;

    return bins.reduce((entropy, count) => {
      if (count === 0) return entropy;
      const p = count / totalSamples;
      return entropy - p * Math.log2(p);
    }, 0);
  }

  /**
   * Signal 5: Maximum Velocity (px/sec).
   * Rejects unnatural coordinate teleportation or macro clicks.
   */
  public static calculateMaxVelocity(points: TrajectoryPoint[]): number {
    let maxV = 0;

    for (let i = 1; i < points.length; i++) {
      const dtSec = (points[i].t - points[i - 1].t) / 1000;
      if (dtSec <= 0) continue;

      const dist = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
      const velocity = dist / dtSec;
      if (velocity > maxV) {
        maxV = velocity;
      }
    }

    return maxV;
  }
}
